import React, { useEffect, useRef, useState } from 'react'
import styles from './vacancy.module.scss'
import cn from 'classnames'
import { useSelector } from 'react-redux'
import VacancyDetailsLabel from '../../../UI/VacancyDetailsLabel/VacancyDetailsLabel'
import AnimateHeight from 'react-animate-height'
import VacancyRightColumn from './VacancyComponents/VacancyRightColumn'
import { ReactComponent as ArrowDownIcon } from '../../../assets/icons/arrow-down.svg'

import VacancyFooter from './VacancyComponents/VacancyFooter'
import VacancyMainInfo from './VacancyComponents/VacancyMainInfo'
import ShareBlock from '../../ShareBlock/ShareBlock'
import { isDefaultVesselImg } from '../../../helpers/helpers'
import ImageComponent from '../../../UI/Image/ImageComponent'
import UseSmartTooltip from '../../../hooks/useSmartTooltip'
import Button from '../../../UI/Button/Button'
import { getDateByUserFormat } from '../../../helpers/timeHelper'

const Vacancy = ({
  vacancy,
  appliedJobs,
  count,
  onClick,
  isOpen,
  refs,
  onAnimationEnd,
  isScrollList,
  variant = 'sea',
  canDelete,
}) => {
  const [loading, setLoading] = useState(false)
  const [isShareBlockOpen, setIsShareBlockOpen] = useState(false)

  const shareBlockWrapperRef = useRef(null)

  const isAdmin = useSelector(({ authentication }) => authentication.user.isAdmin)

  const isLand = variant === 'land'

  const resolvedCanDelete = typeof canDelete === 'boolean' ? canDelete : vacancy?.can_delete ?? vacancy?.canDelete

  const handleShareBlockClose = (e) => {
    e?.stopPropagation?.()
    setIsShareBlockOpen(false)
  }

  const handleToggleOpen = () => {
    if (!isLand && isShareBlockOpen && isOpen) return
    setIsShareBlockOpen(false)
    onClick(vacancy.id, isOpen)
  }

  useEffect(() => {
    if (!isShareBlockOpen) return

    const handlePointerDownOutside = (event) => {
      const root = shareBlockWrapperRef.current
      if (!root) return

      if (!root.contains(event.target)) {
        setIsShareBlockOpen(false)
      }
    }

    document.addEventListener('pointerdown', handlePointerDownOutside, true)

    return () => {
      document.removeEventListener('pointerdown', handlePointerDownOutside, true)
    }
  }, [isShareBlockOpen])

  return (
    <div
      ref={refs}
      className={cn(styles.vacancy, {
        [styles.row_is_scroll_list]: isScrollList,
      })}
    >
      <div
        ref={shareBlockWrapperRef}
        className={cn(styles.shareBlockWrapper, { [styles.shareBlockWrapper_open]: isShareBlockOpen })}
      >
        <ShareBlock slug={vacancy?.slug_name} onClose={handleShareBlockClose} isOpen={isShareBlockOpen} />
      </div>

      <div className={styles.vacancyInfo}>
        <div
          className={cn(styles.vacancyInfo_block, { [styles.vacancyInfo_block_disabled]: isShareBlockOpen && isOpen })}
        >
          <div className={styles.vacancyFullInfo}>
            <div className={styles.header}>
              {isLand ? (
                <div className={styles.row}>
                  <p
                    className={cn(styles.vacancyField, styles.rank, {
                      [styles.rankLand__unviewedVacancy]: vacancy.early_access_flag,
                    })}
                  >
                    {vacancy.rank_name || 'No information'}
                  </p>

                  <div className={styles.bages}>
                    <VacancyDetailsLabel label='shore-based job' />
                    {vacancy.urgent && <VacancyDetailsLabel label='urgent' urgent />}
                  </div>
                </div>
              ) : (
                <div
                  className={cn(styles.vacancyField, {
                    [styles.rank]: true,
                  })}
                >
                  <p className={cn(styles.rank, { [styles.unviewedVacancy]: vacancy.early_access_flag })}>
                    {vacancy.rank?.name}
                  </p>

                  <div className={styles.labels}>
                    {vacancy.urgent && <VacancyDetailsLabel label='urgent' urgent />}

                    <VacancyDetailsLabel label='onboard job' />
                  </div>
                </div>
              )}

              <div className={styles.vacancyField_short}>
                <p>{vacancy.title}</p>
              </div>
            </div>

            <AnimateHeight
              style={{ flexShrink: 0, background: 'var(--color-white)', borderRadius: '8px' }}
              height={isOpen ? 'auto' : 0}
              onAnimationEnd={onAnimationEnd}
            >
              <div className={cn(styles.vacancyFullInfo, styles.body)}>
                {!isLand && vacancy.vessel?.mt_photo_mini_url && (
                  <div className={styles.vacancyField}>
                    <ImageComponent
                      src={vacancy.vessel?.mt_photo_mini_url}
                      alt='vassel'
                      className={cn(styles.vesselPreview, {
                        [styles.vesselPreview_default]: isDefaultVesselImg(vacancy.vessel?.mt_photo_mini_url),
                      })}
                    />
                    <p className={styles.vacancyFieldName}>Vessel name:</p>
                    <p className={cn({ [styles.vesselName]: vacancy.vessel.name })}>{vacancy?.vessel?.name || '—'}</p>
                  </div>
                )}

                <VacancyMainInfo vacancy={vacancy} />
              </div>
              <div className={cn(styles.vacancyFullInfo, styles.footer, { [styles.footerLand]: isLand })}>
                {isLand && (
                  <>
                    <div className={styles.vacancyField}>
                      <p className={styles.vacancyFieldName}>City:</p>
                      <p>{vacancy.city || '—'}</p>
                    </div>
                    <div className={styles.vacancyField}>
                      <p className={styles.vacancyFieldName}>Country:</p>
                      <p>{vacancy.country?.name || '—'}</p>
                    </div>
                  </>
                )}
                {vacancy.vessel?.imo && (
                  <>
                    <div className={styles.vacancyField}>
                      <p className={styles.vacancyFieldName}>Crew:</p>
                      <p>{vacancy.vessel.crew_mixed ? 'Mix' : vacancy.vessel.crew_nationality?.name || '—'}</p>
                    </div>

                    <div className={styles.vacancyField}>
                      <p className={styles.vacancyFieldName}>DWT:</p>
                      <p>{vacancy.vessel.dwt || '—'}</p>
                    </div>

                    <div className={styles.vacancyField}>
                      <p className={styles.vacancyFieldName}>Engine type:</p>
                      <p>{vacancy.vessel.engine_type?.name || '—'}</p>
                    </div>

                    <div className={styles.vacancyField}>
                      <p className={styles.vacancyFieldName}>GRT:</p>
                      <p>{vacancy.vessel.grt || '—'}</p>
                    </div>

                    <div className={styles.vacancyField}>
                      <p className={styles.vacancyFieldName}>Build year:</p>
                      <p>{vacancy.vesselBuildYear || '—'}</p>
                    </div>

                    <div className={styles.vacancyField}>
                      <p className={styles.vacancyFieldName}>TEU:</p>
                      <p>{vacancy.vessel.teu || '—'}</p>
                    </div>

                    <div className={styles.vacancyField}>
                      <p className={styles.vacancyFieldName}>Vessel flag:</p>
                      <p>{vacancy.vessel.flag?.name || '—'}</p>
                    </div>

                    <div className={styles.vacancyField}>
                      <p className={styles.vacancyFieldName}>Power:</p>
                      <p>{vacancy.power}</p>
                    </div>
                  </>
                )}
              </div>
            </AnimateHeight>
          </div>
        </div>
        <VacancyFooter vacancy={vacancy} loading={loading} setLoading={setLoading} isAdmin={isAdmin} isOpen={isOpen} />
        {vacancy.published_at && (
          <p className={styles.publicationDate}>
            Publication date: {getDateByUserFormat(vacancy.published_at)}
          </p>
        )}
        <VacancyRightColumn
          loading={loading}
          vacancy={vacancy}
          setLoading={setLoading}
          setIsShareBlockOpen={setIsShareBlockOpen}
          isShareBlockOpen={isShareBlockOpen}
          count={count}
          isOpen={isOpen}
          onClick={onClick}
          variant={variant}
          appliedJobs={appliedJobs}
          canDelete={resolvedCanDelete}
        />
        <div className={styles.views}>
          <Button theme={'text'} onClick={handleToggleOpen} className={styles.toOpenBlock}>
            <ArrowDownIcon
              className={cn(styles.ArrowIcon, {
                [styles.flippedArrowIcon]: isOpen,
              })}
            />
          </Button>
        </div>
      </div>
    </div>
  )
}

export default Vacancy
